// The blanks a form DRAWS instead of typing.
//
// CONSUMER: shared (web prep screen + node-pool tests).
//
// Nate Swartz, 2026-09-08, on the AAR Residential Listing Contract:
// "Fields are still not mapped well... one of the biggest advantages of
// BrokerMint is that they manage all these forms for us and remap them
// once they come out."
//
// WHY THE LADDER MISSED ALMOST EVERYTHING ON THAT CONTRACT
//
// The listing contract carries roughly 75 fillable components. The
// suggestion ladder found seven. Not because the rungs are weak, but
// because AAR's forms are built in a way all three free rungs are blind
// to:
//
//   * There are no AcroForm widgets (rung 2 finds nothing).
//   * The blanks are DRAWN as vector rules, not typed as underscore
//     runs (rung 3's UNDERSCORE_RUN never matches).
//   * The captions sit BELOW their line in small capitals — "OWNER'S
//     PRINTED NAME", "TELEPHONE", "EMAIL" — while rung 3's caption path
//     places a field to the RIGHT of a label.
//
// So the one thing actually present on every page of an AAR form, the
// drawn line itself, was invisible to the OS. This module reads it.
//
// A PDF's page content is a list of drawing operations, and a ruled
// blank is a horizontal path in that list with exact endpoints. That is
// better evidence than any heuristic over text: the form is telling us,
// to the point, where it left room to be filled in. Same reasoning that
// puts the AcroForm rung above the text rung — read what the document
// declares before guessing at what it means.
//
// THE PRECISION RULE, AND WHY IT SHAPES THE WHOLE MODULE
//
// A page also draws table borders, header rules, footer rules, boxes
// around notices, and the margin lines AAR prints down the side. Those
// are furniture, not blanks, and nothing in the geometry tells them
// apart from a signature line.
//
// The caption does. So this module splits its answer in two:
//
//   * A blank with a caption we can read becomes a PROPOSED FIELD,
//     typed from that caption (a signature, initials, a date, or a data
//     blank bound to the record field the caption names).
//   * A blank with no readable caption becomes an UNMAPPED BLANK — not
//     a field, just a place the prep screen outlines faintly so a
//     person can click it and say what it is in one tap.
//
// That split is what lets detection be generous and proposals stay
// precise. A header rule with no caption is an outline nobody clicks;
// it never becomes a field on a client's legal document. And the count
// of unmapped blanks is the first honest answer the screen has ever had
// to "what did you miss?" — before this, a form with 68 unfound blanks
// and a form with none looked identical.
//
// Pure and dependency-free, the field-types.ts rule: the browser
// extracts raw geometry, this decides what it means, and a node-pool
// test exercises the deciding directly.

import {
  bindingForLabel,
  learnedCeremonyForLabel,
  type BindingLookup,
} from "./label-bindings";
import {
  ceremonyTypeForLabel,
  isProviderStamp,
  roleWordFromCaption,
  type SuggestedField,
  type SpanInput,
} from "./field-suggestions";
import {
  LINE_BOX_RATIO,
  MAX_FIELD_FONT_PT,
  MIN_FIELD_FONT_PT,
  type EsignFieldType,
} from "./field-types";
import {
  MAX_LINE_PITCH_PT,
  MIN_LINE_PITCH_PT,
  MULTILINE_RULE_OVERHANG_PT,
  multilineBoxFromRules,
  multilineRuleYs,
} from "./multiline-text";

/** One painted path off the page, already flattened to its bounding box
 *  in the space every other e-sign module uses: PDF points, top-left
 *  origin, y down, 1-based pages.
 *
 *  The browser side (render-pdf-pages.ts) walks the operator list and
 *  applies the current transform, so by the time a path arrives here it
 *  is a rectangle on the page and nothing about PDF coordinate spaces
 *  is left to reason about. */
export interface VectorPathInput {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Whether the path was stroked (an outline) or filled (a solid). A
   *  ruled blank is normally stroked; some generators draw the same
   *  line as a very thin filled rectangle, so both are accepted. */
  paint: "stroke" | "fill";
  /** Stroke width in points when it was painted. A stroked horizontal
   *  line has zero bbox height, so this is the only measure of how
   *  thick it actually is — and a 6pt bar is a design element, not a
   *  blank. */
  lineWidth: number;
}

/** A place the form left room to be filled in. */
export interface DetectedBlank {
  /** "line" — a ruled blank someone writes on.
   *  "box"  — a small square someone checks. */
  kind: "line" | "box";
  pageNumber: number;
  /** The FIELD's box, not the path's: for a line, a field-height box
   *  sitting on the rule the way handwriting sits on it. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** The rule's own y, kept so a caption can be measured against the
   *  line itself rather than against the box we invented around it. For
   *  a multi-line run this is the FIRST rule's. */
  lineY: number;
  /** A RUN OF STACKED RULES READ AS ONE WRITING AREA (Grant, 2026-09-29).
   *
   *  "8. Additional terms and conditions should absolutely be one text
   *  field." Forty-three rules at a steady 14pt pitch with nothing
   *  printed between them are one place to write, not forty-three; the
   *  same for lines 12–16 on page 1 of the purchase contract. `lineCount`
   *  says how many rules the box spans and `linePitch` the points
   *  between them (the one number every derivation comes from — see
   *  shared/esign/multiline-text.ts); `firstLineIndent` is how far in
   *  the first line starts when the form printed a caption on its rule.
   *  All absent on an ordinary single-rule blank. */
  lineCount?: number;
  linePitch?: number;
  firstLineIndent?: number;
}

// ─── Reading the page's drawing instructions ────────────────────────

/** The pdf.js opcode numbers this walk needs, passed in rather than
 *  imported so the shared module never depends on pdfjs and a node test
 *  can hand over the same numbers the browser uses. */
export interface PdfOpCodes {
  save: number;
  restore: number;
  transform: number;
  setLineWidth: number;
  rectangle: number;
  constructPath: number;
  fill: number;
  eoFill: number;
}

/** Stop reading a page's paths after this many. A page of a contract
 *  draws a few hundred; a map or a chart draws tens of thousands, and
 *  the blanks on such a page were never going to be found this way. */
const MAX_VECTOR_PATHS_PER_PAGE = 4000;

/** Matrix compose, canvas order: `apply(next)` happens BEFORE `base`.
 *  Same arithmetic as pdf.js's own Util.transform — re-stated here
 *  rather than imported because pdf.js does not export it, and getting
 *  the multiplication order backwards would put every path on the page
 *  mirrored, which is the kind of wrong that still looks plausible. */
function composeMatrix(base: number[], next: number[]): number[] {
  return [
    base[0]! * next[0]! + base[2]! * next[1]!,
    base[1]! * next[0]! + base[3]! * next[1]!,
    base[0]! * next[2]! + base[2]! * next[3]!,
    base[1]! * next[2]! + base[3]! * next[3]!,
    base[0]! * next[4]! + base[2]! * next[5]! + base[4]!,
    base[1]! * next[4]! + base[3]! * next[5]! + base[5]!,
  ];
}

function applyMatrix(m: number[], x: number, y: number): [number, number] {
  return [m[0]! * x + m[2]! * y + m[4]!, m[1]! * x + m[3]! * y + m[5]!];
}

/**
 * Walk a page's drawing instructions and return every painted path as a
 * box on the page.
 *
 * Takes pdf.js's opcode numbers as an ARGUMENT rather than importing
 * them, which is the whole reason this lives here instead of in the
 * browser module: the same walk can then be run in a node test against
 * a real PDF, and the thing that decides where a signature line is on a
 * client's contract is proven rather than assumed.
 *
 * Two things this does that a naive read would get wrong:
 *
 *   * IT TRACKS THE TRANSFORM. Path coordinates are in whatever space
 *     the content stream is currently in. A checkbox drawn as a 9×9
 *     square translated into place reports coordinates 0..9 — useless
 *     without the matrix that puts it on the page. The caller seeds the
 *     stack with the scale-1 viewport transform, which also carries the
 *     top-left flip and any page rotation, so paths land in the same
 *     space as spans and widgets with no second conversion anywhere.
 *
 *   * IT SPLITS SUBPATHS. Generators batch many lines into one path
 *     operation. Taking the operation's bounding box would turn a table
 *     of twelve rules into one enormous rectangle covering the page.
 *     Each subpath gets its own box.
 *
 * Never throws: a malformed or unfamiliar operator list yields fewer
 * paths, never an exception, because a page whose drawing can't be read
 * must still render and still take hand-placed fields.
 */
export function pathsFromOperatorList(
  opList: { fnArray: unknown; argsArray: unknown },
  OPS: PdfOpCodes,
  where: { viewportTransform: number[]; pageNumber: number },
): VectorPathInput[] {
  const fnArray = opList.fnArray as ArrayLike<number> | undefined;
  const argsArray = opList.argsArray as ArrayLike<unknown> | undefined;
  if (!fnArray || !argsArray) return [];
  const { viewportTransform, pageNumber } = where;
  const out: VectorPathInput[] = [];

  let ctm = [...viewportTransform];
  let lineWidth = 1;
  const stack: { ctm: number[]; lineWidth: number }[] = [];

  const push = (
    minX: number,
    minY: number,
    maxX: number,
    maxY: number,
    paint: "stroke" | "fill",
  ) => {
    if (out.length >= MAX_VECTOR_PATHS_PER_PAGE) return;
    if (![minX, minY, maxX, maxY].every((n) => Number.isFinite(n))) return;
    // The pen has width in the CTM's space too, so a page drawn at half
    // scale has half-width strokes. sqrt(|det|) is the isotropic part of
    // the matrix, which is what a thickness test wants.
    const scale = Math.sqrt(Math.abs(ctm[0]! * ctm[3]! - ctm[1]! * ctm[2]!)) || 1;
    out.push({
      pageNumber,
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      paint,
      lineWidth: paint === "stroke" ? lineWidth * scale : 0,
    });
  };

  /** One subpath's box, in page space. */
  const emitSubpath = (points: [number, number][], paint: "stroke" | "fill") => {
    if (points.length < 2) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [px, py] of points) {
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }
    push(minX, minY, maxX, maxY, paint);
  };

  for (let i = 0; i < fnArray.length; i++) {
    const fn = fnArray[i];
    const args = argsArray[i] as unknown[];

    if (fn === OPS.save) {
      stack.push({ ctm: [...ctm], lineWidth });
      continue;
    }
    if (fn === OPS.restore) {
      const prev = stack.pop();
      if (prev) {
        ctm = prev.ctm;
        lineWidth = prev.lineWidth;
      }
      continue;
    }
    if (fn === OPS.transform) {
      const m = (args as number[]).slice(0, 6);
      if (m.length === 6 && m.every((n) => Number.isFinite(n))) {
        ctm = composeMatrix(ctm, m);
      }
      continue;
    }
    if (fn === OPS.setLineWidth) {
      const w = Number((args as number[])[0]);
      if (Number.isFinite(w)) lineWidth = w;
      continue;
    }
    // A rectangle op, where a generator emits one instead of a path.
    if (fn === OPS.rectangle) {
      const [rx, ry, rw, rh] = (args as number[]).map(Number);
      if ([rx, ry, rw, rh].every((n) => Number.isFinite(n))) {
        const corners: [number, number][] = [
          applyMatrix(ctm, rx!, ry!),
          applyMatrix(ctm, rx! + rw!, ry!),
          applyMatrix(ctm, rx! + rw!, ry! + rh!),
          applyMatrix(ctm, rx!, ry! + rh!),
        ];
        emitSubpath(corners, "stroke");
      }
      continue;
    }
    if (fn !== OPS.constructPath) continue;

    // [paintOp, [flatPathArray], minMax] — see the DrawOPS encoding in
    // pdfjs-dist's own canvas layer. The flat array interleaves an
    // operator with its coordinates: moveTo/lineTo take 2, curveTo 6,
    // closePath none.
    const paintOp = Number(args[0]);
    const data = args[1] as unknown[];
    const flat = Array.isArray(data) ? (data[0] as unknown) : null;
    if (!flat || typeof (flat as { length?: unknown }).length !== "number") continue;
    const path = flat as ArrayLike<number>;
    const paint: "stroke" | "fill" =
      paintOp === OPS.fill || paintOp === OPS.eoFill ? "fill" : "stroke";

    let points: [number, number][] = [];
    for (let j = 0; j < path.length; ) {
      const op = path[j++];
      if (op === 0 /* moveTo */) {
        // A moveTo starts a new subpath — flush what came before, so a
        // batch of lines in one operation stays a batch of lines.
        emitSubpath(points, paint);
        points = [applyMatrix(ctm, Number(path[j++]), Number(path[j++]))];
      } else if (op === 1 /* lineTo */) {
        points.push(applyMatrix(ctm, Number(path[j++]), Number(path[j++])));
      } else if (op === 2 /* curveTo */) {
        // Only the endpoint matters for a bounding box we intend to
        // classify as "a straight rule or not" — and a curve is neither
        // a rule nor a checkbox, so precision here buys nothing.
        j += 4;
        points.push(applyMatrix(ctm, Number(path[j++]), Number(path[j++])));
      } else if (op === 3 /* closePath */) {
        if (points.length > 0) points.push(points[0]!);
      } else {
        break; // an operator we don't know — stop rather than misread
      }
      if (out.length >= MAX_VECTOR_PATHS_PER_PAGE) break;
    }
    emitSubpath(points, paint);
  }

  return out;
}


// ─── What counts as a blank ─────────────────────────────────────────

/** Above this a horizontal path is a bar, a shaded band or a table
 *  edge with weight — not a line someone writes on. */
const MAX_RULE_THICKNESS_PT = 3;
/** A signature box drawn round a signing area: as wide as a signature
 *  line, a signature's height. Narrower or shorter is a checkbox or a
 *  rule; taller is a frame around a section. */
const SIGNATURE_BOX_MIN_WIDTH_PT = 120;
const SIGNATURE_BOX_MIN_HEIGHT_PT = 14;
const SIGNATURE_BOX_MAX_HEIGHT_PT = 60;

/** Below this a horizontal path is a tick, a bullet rule or a fragment
 *  of a table border. Nobody writes anything in 24 points. */
const MIN_RULE_WIDTH_PT = 24;

/** A path at least this fraction of the page's width is a divider or a
 *  page border, drawn edge to edge. Deliberately high (a genuine
 *  full-line blank on a contract still starts after a caption and stops
 *  before the margin), and deliberately the ONLY furniture rule with
 *  teeth — everything else is filtered by "does it have a caption?"
 *  rather than by geometry. */
const PAGE_WIDE_RULE_RATIO = 0.92;

/** A checkbox is small and roughly square. AAR's election boxes run
 *  about 9pt; a 3pt square is a bullet and a 30pt one is a panel. */
const MIN_BOX_PT = 5;
const MAX_BOX_PT = 20;
const MAX_BOX_ASPECT = 1.7;

/** Two rules on the same baseline this close together are one blank
 *  drawn in pieces (common — a generator emits a segment per style
 *  change). Joined so a 200pt signature line drawn as four segments
 *  proposes ONE signature field rather than four stubby ones. */
const JOIN_GAP_PT = 4;
/** How far apart two rules' baselines may be and still be the same
 *  line. Sub-point differences are rounding in the transform. */
const SAME_BASELINE_PT = 1.2;

/** Field heights, matching the prep screen's own defaults so a detected
 *  field and a hand-placed one are indistinguishable once accepted. */
const FIELD_HEIGHT: Record<EsignFieldType, number> = {
  signature: 28,
  initials: 28,
  date_signed: 24,
  text: 24,
  checkbox: 16,
  dropdown: 24,
  radio: 16,
  editable_date: 24,
  attachment: 28,
};

/** How far a field's box hangs below the rule it sits on. Handwriting
 *  crosses the line slightly; a box flush to it looks misaligned. The
 *  same number a multi-line box uses for every one of its rules, read
 *  from the one place that defines it. */
const RULE_OVERHANG_PT = MULTILINE_RULE_OVERHANG_PT;

/** However small a form's print gets, a box below this is neither
 *  readable nor tappable. Matches the prep screen's own floor. */
const MIN_TEXT_BOX_PT = 12;

/**
 * The size this page's BODY TYPE is set in.
 *
 * The caption beside a blank is the precise answer to "what should the
 * value on this line print at", and every newly read document now
 * carries it. This is the answer for everything else: a box placed by
 * hand, and — the case that matters most — a box that comes back from a
 * map saved before any of this existed. Baker's StewDocs map is exactly
 * that, and the whole point of reading it off the page is that his map
 * gets the right answer without being re-taught.
 *
 * WEIGHTED BY CHARACTERS, not by span count. A disclosure has one
 * 16-point heading and four hundred words of 9-point body; counting
 * spans lets a handful of headings and table labels outvote the prose,
 * while counting the characters they contain cannot. Rounded into
 * half-point buckets first, because the same run of type comes back
 * from pdf.js as 9.0 and 9.000001 depending on the transform.
 *
 * Null when the page has no usable text — a scan, or a page of pure
 * artwork. The caller keeps its own default rather than being handed a
 * number this function had to invent.
 */
export function dominantTypeSize(
  spans: readonly SpanInput[],
  pageNumber: number,
): number | null {
  const weight = new Map<number, number>();
  for (const s of spans) {
    if (s.pageNumber !== pageNumber) continue;
    const chars = s.text.trim().length;
    if (chars === 0) continue;
    if (!Number.isFinite(s.height)) continue;
    if (s.height < MIN_FIELD_FONT_PT || s.height > MAX_FIELD_FONT_PT) continue;
    const bucket = Math.round(s.height * 2) / 2;
    weight.set(bucket, (weight.get(bucket) ?? 0) + chars);
  }
  let best: number | null = null;
  let bestWeight = 0;
  for (const [size, w] of weight) {
    // Ties go to the SMALLER size: body type is the thing being looked
    // for, and on a page where two sizes are genuinely neck and neck
    // the larger one is a heading or a table header.
    if (w > bestWeight || (w === bestWeight && best !== null && size < best)) {
      best = size;
      bestWeight = w;
    }
  }
  return best;
}

export interface PageSize {
  pageNumber: number;
  width: number;
  height: number;
}

// ─── A rule that is actually a table's floor, not a blank ───────────
//
// Joey Ellis, 2026-09-18, opening a plain cover letter: the OS outlined
// 42 "blanks" on a one-page welcome letter that has nothing to sign.
// Every one was the bottom border of a row in one of the letter's three
// informational tables (what's in this package, do/avoid, the notice
// box). Those borders are exactly as wide as a real blank, exactly as
// thin, and never span the page — nothing in `detectBlanks`'s geometry
// rules above tells them apart from a signature line, on purpose: the
// header explains why precision is the CAPTION's job, not this one's.
//
// A caption never got attached to these because a table cell's content
// is a paragraph — `captionForBlank`'s MAX_CAPTION_CHARS gate correctly
// refuses to read "Arizona and federal law (RESPA) requires us to tell
// you that..." as a caption naming a blank. So no field was proposed,
// which is right. But the underlying rule still came out of `detectBlanks`
// as an uncaptioned BLANK, and `unmappedBlanks` counts every one of
// those as something the sender still has to answer. A form with a
// filled table and a form with 42 genuine unanswered blanks looked
// identical, which is the same failure the whole unmapped-count feature
// exists to prevent for the opposite case.
//
// The distinguishing fact on the page is where the WRITING SPACE for a
// real blank actually is. A signature line has empty air above it
// (that's what the line is for) and, at most, one short caption line
// naming it — below, beside, or in AAR's rarer stacked case, above. A
// table row's border has the row's own PRINTED CONTENT sitting directly
// above it, wrapped across more than one line, and running most of the
// border's own width — because the border closes the cell, it doesn't
// invite anyone to write in it.
//
// Two lines of text is the line: a single caption above a blank
// (`captionForBlank`'s rank-4 shape, "OWNER'S SIGNATURE" above its own
// rule) is one printed line. A wrapped table cell is never just one.
// Requiring both a second line AND real width coverage is what keeps a
// short above-caption a real field while a paragraph's last two wrapped
// lines read as furniture.

/** How far above a rule to look for a cell's printed content. Deep
 *  enough to reach a wrapped paragraph's last couple of lines at this
 *  letter's ~9-11pt body size with normal leading; a real above-caption
 *  sits well inside this too, so the line-count and coverage checks
 *  below are what carry the distinction, not this reach. */
const PROSE_ZONE_ABOVE_PT = 60;

/** Spans within this many points of each other's baseline are the same
 *  printed line — comfortably under a real line-height gap, so two
 *  words on one wrapped line never count as two lines. */
const SAME_TEXT_LINE_PT = 3;

/** Fewer than this is an ordinary caption (one printed line above the
 *  rule). This many or more is a cell's wrapped content — nobody
 *  captions a blank with two stacked lines of prose. */
const PROSE_MIN_DISTINCT_LINES = 2;

/** How much of the rule's own width that prose must cover to count as
 *  occupying its writing space, rather than merely sitting somewhere
 *  above it. Higher than `MIN_OVERLAP_RATIO` (which only needs enough
 *  overlap to say a short caption belongs to this rule) because the
 *  question here is different: does printed content fill the space a
 *  field box would otherwise sit in. */
const PROSE_COVERAGE_RATIO = 0.5;

/** The combined width a set of spans covers of one horizontal span,
 *  merging overlaps so text broken into several runs (a justified
 *  line, a mid-sentence font change) isn't double-counted. */
function unionOverlapWidth(
  spans: readonly { x: number; width: number }[],
  line: { x: number; width: number },
): number {
  const clipped = spans
    .map((s) => [Math.max(s.x, line.x), Math.min(s.x + s.width, line.x + line.width)] as const)
    .filter(([left, right]) => right > left)
    .sort((a, b) => a[0] - b[0]);
  let covered = 0;
  let curStart = 0;
  let curEnd = -Infinity;
  for (const [left, right] of clipped) {
    if (left > curEnd) {
      if (curEnd > curStart) covered += curEnd - curStart;
      curStart = left;
      curEnd = right;
    } else {
      curEnd = Math.max(curEnd, right);
    }
  }
  if (curEnd > curStart) covered += curEnd - curStart;
  return covered;
}

/** True when a rule's writing space is already filled by a table cell's
 *  content rather than left empty for someone to sign or write on. See
 *  the header above this block for why "prose", not a caption match, is
 *  the test. */
function hasProseAbove(
  rule: { pageNumber: number; x: number; width: number; lineY: number },
  spans: readonly SpanInput[],
): boolean {
  const zone = spans.filter((s) => {
    if (s.pageNumber !== rule.pageNumber) return false;
    if (!s.text.trim()) return false;
    // ONLY TEXT OVER THIS RULE (2026-10-14). The margin's line numbers,
    // the other column's text and a signing service's stamp sat in the
    // zone too and counted as a second "line of prose", so the right-hand
    // signature line under AAR's one-sentence "The undersigned agrees…"
    // was thrown out as a table border on Nate's Addendum.
    if (s.x + s.width <= rule.x || s.x >= rule.x + rule.width) return false;
    if (isProviderStamp(s.text) || isSignedStamp(s.text)) return false;
    const bottom = s.y + s.height;
    // Sitting right above the rule (a couple points of slack for
    // rounding), and not so far up that this reaches the PREVIOUS row's
    // content instead of this row's.
    return bottom <= rule.lineY + 2 && bottom >= rule.lineY - PROSE_ZONE_ABOVE_PT;
  });
  if (zone.length === 0) return false;

  const distinctLines = new Set(zone.map((s) => Math.round(s.y / SAME_TEXT_LINE_PT)));
  if (distinctLines.size < PROSE_MIN_DISTINCT_LINES) return false;

  return unionOverlapWidth(zone, rule) / Math.max(1e-6, rule.width) >= PROSE_COVERAGE_RATIO;
}

/**
 * Every place the form drew room to be filled in.
 *
 * Generous on purpose — see the header. Precision is the caption's job
 * (`suggestFromBlanks`), not this function's, because a blank with no
 * caption is still a real blank and the prep screen wants to show it.
 */
export function detectBlanks(
  paths: readonly VectorPathInput[],
  pageSizes: readonly PageSize[],
  /** The page's text, so a table's own row borders can be told apart
   *  from a real blank — see the header above `hasProseAbove`. Optional
   *  because a caller with no text layer handy (a test exercising pure
   *  geometry) still gets every line and box; it just can't rule out
   *  furniture. */
  spans?: readonly SpanInput[],
): DetectedBlank[] {
  const byPage = new Map(pageSizes.map((p) => [p.pageNumber, p]));
  const rules: VectorPathInput[] = [];
  const boxes: DetectedBlank[] = [];

  for (const p of paths) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    if (!Number.isFinite(p.width) || !Number.isFinite(p.height)) continue;
    const page = byPage.get(p.pageNumber);

    // A small, roughly-square outline is a checkbox. Filled squares are
    // excluded: a solid black 9pt square is a bullet or a filled-in
    // marker, not somewhere to tick.
    if (
      p.paint === "stroke" &&
      p.width >= MIN_BOX_PT &&
      p.height >= MIN_BOX_PT &&
      p.width <= MAX_BOX_PT &&
      p.height <= MAX_BOX_PT &&
      Math.max(p.width, p.height) / Math.max(1e-6, Math.min(p.width, p.height)) <= MAX_BOX_ASPECT
    ) {
      boxes.push({
        kind: "box",
        pageNumber: p.pageNumber,
        x: p.x,
        y: p.y,
        width: p.width,
        height: p.height,
        lineY: p.y + p.height,
      });
      continue;
    }

    // A SIGNING SERVICE'S SIGNATURE BOX (2026-10-14). dotloop draws a
    // rectangle round every signature area on an export, sitting on the
    // form's own line. Its bottom edge is that line, whether or not the
    // form's rule survived the export; it is kept as one, and the rule
    // de-duplication below folds it into the form's own if both exist.
    if (
      p.paint === "stroke" &&
      p.width >= SIGNATURE_BOX_MIN_WIDTH_PT &&
      p.height >= SIGNATURE_BOX_MIN_HEIGHT_PT &&
      p.height <= SIGNATURE_BOX_MAX_HEIGHT_PT &&
      (!page || p.width < page.width * PAGE_WIDE_RULE_RATIO) &&
      // Only a box the page itself marks as a signing block: a date
      // caption or the word "signature" printed just under it. A table's
      // cells are rectangles too, and must not become lines to sign on.
      !!spans?.some((sp) => {
        if (sp.pageNumber !== p.pageNumber) return false;
        const gap = sp.y - (p.y + p.height);
        if (gap < -3 || gap > SPLIT_CAPTION_DEPTH_PT) return false;
        const cx = sp.x + sp.width / 2;
        if (cx < p.x || cx > p.x + p.width) return false;
        return DATE_FORMAT_CAPTION.test(sp.text.trim()) || /\bsignature\b/i.test(sp.text);
      })
    ) {
      rules.push({ ...p, y: p.y + p.height, height: 0 });
      continue;
    }

    // A horizontal rule. Thickness comes from the bbox for a fill and
    // from the pen for a stroke, because a stroked line's bbox height
    // is zero however heavy the pen is.
    const thickness = p.paint === "stroke" ? Math.max(p.height, p.lineWidth) : p.height;
    if (p.height > MAX_RULE_THICKNESS_PT) continue;
    if (thickness > MAX_RULE_THICKNESS_PT) continue;
    if (p.width < MIN_RULE_WIDTH_PT) continue;
    if (page && p.width >= page.width * PAGE_WIDE_RULE_RATIO) continue;
    rules.push(p);
  }

  // A rule with words sitting ON it is an underline — a section heading,
  // an emphasised phrase — not somewhere to write. Dropped before
  // anything else looks at it: the purchase contract's "8. ADDITIONAL
  // TERMS AND CONDITIONS" heading rule sits exactly one line-pitch above
  // the section's first writing line and would otherwise open the run
  // (measured 2026-09-29).
  const lines = spans
    ? joinRules(rules).filter((b) => !isUnderline(b, spans))
    : joinRules(rules);
  // STACKED RULES FIRST, then furniture. A run of rules at a steady
  // pitch with nothing printed between them is one writing area, and a
  // member of a run is never a table border: a table's rows have their
  // content between the rules, which is exactly what breaks a run. So
  // the run test is also what rescues the first three lines of the
  // purchase contract's "Additional Terms" section, which the prose
  // check alone threw away because the section heading and the page
  // header sit within its reach above them (measured 2026-09-29: rules
  // 344–346 missing, 347–389 found).
  const { runs, singles } = groupStackedRules(lines, spans ?? []);
  // A checkbox is never a table border — its caption is short by
  // definition (an election word), so only lines need the prose check.
  const realLines = spans ? singles.filter((b) => !hasProseAbove(b, spans)) : singles;

  return [...boxes, ...runs, ...realLines].sort(
    (a, b) => a.pageNumber - b.pageNumber || a.y - b.y || a.x - b.x,
  );
}

// ─── Stacked rules: one writing area, not one blank per line ────────

/** Two rules' edges this close are the same edge. Wider than the
 *  same-baseline tolerance because AAR indents a line that carries a
 *  "$" by about 7pt from the lines around it, and that is jitter in
 *  the layout, not a different column. */
const SAME_EDGE_PT = 10;
/** How much two consecutive gaps in one run may differ and still be
 *  the same pitch. A generator emits rules at a fixed leading; a run
 *  whose gaps wander is two things that happen to be near each other. */
const PITCH_TOLERANCE_PT = 1;
/** A first line may start this far in (as a fraction of the line's
 *  width) and still belong to the run below it — the caption-on-the-
 *  first-rule shape ("Legal Description: ______" then full lines). */
const MAX_FIRST_LINE_INDENT_RATIO = 0.7;

/** Is there printed text in the band between two rules, over the lower
 *  rule's own extent? A caption under a line, a table row's content, a
 *  paragraph — any of them means these two rules are not one area. The
 *  lower rule's extent, not the union's, so a "$" printed at the start
 *  of an indented line (which the rule itself starts after) does not
 *  count as writing between the lines. Margin line numbers sit left of
 *  every rule and never overlap. */
function textBetween(
  upper: DetectedBlank,
  lower: DetectedBlank,
  spans: readonly SpanInput[],
): boolean {
  for (const span of spans) {
    if (span.pageNumber !== lower.pageNumber) continue;
    if (!span.text.trim()) continue;
    const cy = span.y + span.height / 2;
    if (cy <= upper.lineY || cy >= lower.lineY) continue;
    const left = Math.max(span.x, lower.x);
    const right = Math.min(span.x + span.width, lower.x + lower.width);
    if (right - left > 0) return true;
  }
  return false;
}

/** Does `next` continue the run ending in `prev`? */
function continuesRun(
  run: readonly DetectedBlank[],
  next: DetectedBlank,
  spans: readonly SpanInput[],
  lines: readonly DetectedBlank[],
): boolean {
  const prev = run[run.length - 1]!;
  if (next.pageNumber !== prev.pageNumber) return false;
  const gap = next.lineY - prev.lineY;
  if (gap < MIN_LINE_PITCH_PT || gap > MAX_LINE_PITCH_PT) return false;
  if (run.length >= 2) {
    const pitch = (prev.lineY - run[0]!.lineY) / (run.length - 1);
    // RELATIVE, with the old absolute floor (2026-10-13). A re-printed
    // or exported form spaces its rules unevenly by a point or two —
    // 13.2 then 15.6 — and a flat 1pt tolerance closed the run after
    // every second rule: Nate's Addendum came back as fifteen 2-line
    // boxes instead of one 34-line one, and short text in one box left
    // its second line empty so the next box seemed to skip a line.
    if (Math.abs(gap - pitch) > Math.max(PITCH_TOLERANCE_PT, pitch * 0.2)) return false;
  }
  const prevRight = prev.x + prev.width;
  const nextRight = next.x + next.width;
  if (Math.abs(prevRight - nextRight) > SAME_EDGE_PT) return false;
  const sameLeft = Math.abs(prev.x - next.x) <= SAME_EDGE_PT;
  // Only the FIRST line of a run may be indented, and only to the right
  // — and only when it is the only rule on its baseline. A line carrying
  // two rules ("$ ______  Earnest Money ______________") is a form row
  // with two blanks on it, not the captioned first line of a writing
  // area, however neatly the second rule lines up with the lines below.
  const firstIndented =
    run.length === 1 &&
    prev.x > next.x &&
    prev.x - next.x <= next.width * MAX_FIRST_LINE_INDENT_RATIO &&
    !prevSharesBaseline(prev, lines);
  if (!sameLeft && !firstIndented) return false;
  // A line with its OWN caption is its own blank. Words right after the
  // rule ("$ ______ Full Purchase Price") or right before a line that
  // is not the first ("washer (description): ______") name that line
  // alone, so it never joins a run. A bare symbol — the "$" AAR prints
  // at the head of a money line — is not a caption and does not count.
  if (letteredTextBeside(prev, spans, "right") || letteredTextBeside(next, spans, "right")) {
    return false;
  }
  if (letteredTextBeside(next, spans, "left")) return false;
  return !textBetween(prev, next, spans);
}

/** How far past a rule's end a same-line caption may start and still be
 *  that rule's — "$ ______ Full Purchase Price" prints its words a few
 *  points after the line. */
const BESIDE_GAP_PT = 30;
/** Letters, so a "$" or a margin line number is never mistaken for a
 *  caption. */
const HAS_LETTERS = /[a-z]/i;

/** Is there a lettered word on this rule's own baseline, just past its
 *  right end or just before its left end? */
function letteredTextBeside(
  rule: DetectedBlank,
  spans: readonly SpanInput[],
  side: "left" | "right",
): boolean {
  for (const span of spans) {
    if (span.pageNumber !== rule.pageNumber) continue;
    if (!HAS_LETTERS.test(span.text)) continue;
    const bottom = span.y + span.height;
    // On the rule's baseline: the text's bottom within a few points of
    // the rule (type sits on or just above a rule it is beside).
    if (bottom < rule.lineY - span.height || bottom > rule.lineY + 3) continue;
    const gap =
      side === "right" ? span.x - (rule.x + rule.width) : rule.x - (span.x + span.width);
    if (gap >= -2 && gap <= BESIDE_GAP_PT) return true;
  }
  return false;
}

/** Does another rule share this one's baseline? */
function prevSharesBaseline(rule: DetectedBlank, all: readonly DetectedBlank[]): boolean {
  return all.some(
    (o) =>
      o !== rule &&
      o.pageNumber === rule.pageNumber &&
      Math.abs(o.lineY - rule.lineY) <= SAME_BASELINE_PT,
  );
}

/** How close above a rule words must sit to be underlined by it, rather
 *  than captioning a blank from above (which the caption matcher allows
 *  up to 14pt for). A heading's underline sits 4–5pt under its type. */
const UNDERLINE_GAP_PT = 6;

/** Is this rule an underline — words sitting directly on it, over its
 *  own extent — rather than a line left empty to write on? */
function isUnderline(rule: DetectedBlank, spans: readonly SpanInput[]): boolean {
  for (const span of spans) {
    if (span.pageNumber !== rule.pageNumber) continue;
    if (!HAS_LETTERS.test(span.text)) continue;
    const gap = rule.lineY - (span.y + span.height);
    if (gap < -1 || gap > UNDERLINE_GAP_PT) continue;
    // The words must actually sit over the rule, not beside it: a caption
    // printed before a rule on the same line ends where the rule starts.
    const left = Math.max(span.x, rule.x);
    const right = Math.min(span.x + span.width, rule.x + rule.width);
    if (right - left >= Math.min(span.width, rule.width) * 0.5) return true;
  }
  return false;
}

/** One blank for a run of rules, sized so that line k's rule lands
 *  exactly where multiline-text.ts says it does. */
function blankForRun(run: readonly DetectedBlank[]): DetectedBlank {
  const first = run[0]!;
  const last = run[run.length - 1]!;
  const n = run.length;
  const pitch = (last.lineY - first.lineY) / (n - 1);
  // The box's left edge is the rightmost start among the lines AFTER
  // the first (jitter of a few points is absorbed by the widest start,
  // so type never overprints a "$" printed at a line's head); the first
  // line's own start, when it is further right still, becomes the
  // indent the text honours.
  const rest = run.slice(1);
  const left = Math.max(...rest.map((b) => b.x));
  const right = Math.min(...run.map((b) => b.x + b.width));
  const indent = first.x > left + PITCH_TOLERANCE_PT ? first.x - left : 0;
  const box = multilineBoxFromRules(first.lineY, n, pitch);
  return {
    kind: "line",
    pageNumber: first.pageNumber,
    x: left,
    y: Math.max(0, box.y),
    width: Math.max(MIN_RULE_WIDTH_PT, right - left),
    height: box.height,
    lineY: first.lineY,
    lineCount: n,
    linePitch: pitch,
    ...(indent > 0 ? { firstLineIndent: indent } : {}),
  };
}

/**
 * Split the page's rules into stacked runs (each returned as ONE blank
 * spanning every rule in it) and the singles left over.
 *
 * A run is two or more rules, one under another at a steady pitch,
 * sharing their right edge and (after the first) their left edge, with
 * nothing printed between any two of them. That last condition is what
 * keeps this precise: a signature block's captions, a table's cell
 * contents and a paragraph all sit between rules, so none of them ever
 * becomes a run. Only a genuinely empty stack of lines does.
 */
export function groupStackedRules(
  lines: readonly DetectedBlank[],
  spans: readonly SpanInput[],
): { runs: DetectedBlank[]; singles: DetectedBlank[] } {
  const sorted = [...lines].sort(
    (a, b) => a.pageNumber - b.pageNumber || a.lineY - b.lineY || a.x - b.x,
  );
  const runs: DetectedBlank[] = [];
  const singles: DetectedBlank[] = [];
  let run: DetectedBlank[] = [];
  const close = () => {
    if (run.length >= 2) runs.push(blankForRun(run));
    else singles.push(...run);
    run = [];
  };
  for (const line of sorted) {
    if (run.length > 0 && continuesRun(run, line, spans, sorted)) {
      run.push(line);
      continue;
    }
    close();
    run = [line];
  }
  close();
  return { runs, singles };
}

/** Fold segments that are really one line, then turn each into the box
 *  a field would occupy. Also drops duplicates — a generator that
 *  strokes AND fills the same rule emits it twice, and two proposals on
 *  one line is the thing `mergeSuggestions` exists to prevent
 *  downstream, cheaper to avoid here. */
function joinRules(rules: readonly VectorPathInput[]): DetectedBlank[] {
  const sorted = [...rules].sort(
    (a, b) => a.pageNumber - b.pageNumber || a.y - b.y || a.x - b.x,
  );
  const merged: { pageNumber: number; y: number; left: number; right: number }[] = [];

  for (const r of sorted) {
    const left = r.x;
    const right = r.x + r.width;
    const open = merged.find(
      (m) =>
        m.pageNumber === r.pageNumber &&
        ((Math.abs(m.y - r.y) <= SAME_BASELINE_PT &&
          // Touching, overlapping, or separated by less than a word space.
          left <= m.right + JOIN_GAP_PT &&
          right >= m.left - JOIN_GAP_PT) ||
          // THE SAME RULE DRAWN TWICE, a point or two apart (2026-10-13):
          // closer than any two writing lines can be, and covering most
          // of each other. Kept apart, the pair breaks every run it sits
          // in — the second copy is "a line" too close to be one.
          (Math.abs(m.y - r.y) < MIN_LINE_PITCH_PT &&
            Math.min(m.right, right) - Math.max(m.left, left) >=
              0.5 * Math.min(m.right - m.left, right - left))),
    );
    if (open) {
      open.left = Math.min(open.left, left);
      open.right = Math.max(open.right, right);
      // Keep the baseline at the first segment's — they are within
      // SAME_BASELINE_PT by construction, and drifting it as segments
      // join would let a run of near-misses wander off the line.
      continue;
    }
    merged.push({ pageNumber: r.pageNumber, y: r.y, left, right });
  }

  return merged
    .filter((m) => m.right - m.left >= MIN_RULE_WIDTH_PT)
    .map((m) => ({
      kind: "line" as const,
      pageNumber: m.pageNumber,
      x: m.left,
      y: m.y,
      width: m.right - m.left,
      // Height is settled per-type in suggestFromBlanks; this is the
      // text default so an unmapped outline is a sensible size to click.
      height: FIELD_HEIGHT.text,
      lineY: m.y,
    }))
    .map((b) => ({ ...b, y: Math.max(0, b.lineY - b.height + RULE_OVERHANG_PT) }));
}

/** A stable identity for a blank, so the screen can key an outline on it
 *  and scroll to it. Position-derived rather than random: the same PDF
 *  re-read gives the same keys, so an outline does not remount and lose
 *  its place every time the suggestion pass runs again. */
export function blankKey(b: DetectedBlank): string {
  return `${b.pageNumber}:${Math.round(b.x)}:${Math.round(b.lineY)}:${b.kind}`;
}

// ─── Which caption belongs to which blank ───────────────────────────

/** How far below a rule its caption may sit. AAR prints these in ~6.5pt
 *  capitals a few points under the line; two lines of that is the most
 *  a caption can honestly be. */
const BELOW_GAP_PT = 16;

/** How far above a rule a caption may sit. Same size, and deliberately
 *  ranked BELOW the below-caption shape — in a stacked signature block
 *  the thing above a line is the PREVIOUS line's caption, and treating
 *  it as this line's would label every field with its neighbour's
 *  meaning. */
const ABOVE_GAP_PT = 14;

/** A caption immediately left of a rule, on the same line — "Assessor's
 *  #: ______". Tight, because this shape outranks everything and a
 *  generous reach would let the last word of a paragraph claim a line. */
const TIGHT_LEFT_GAP_PT = 40;

/** A caption further left on the same line. Still real (forms pad
 *  between a label and its rule), just not as certain. */
const WIDE_LEFT_GAP_PT = 240;

/** Longer than this and a run of text under a line is prose that
 *  happens to be there, not a caption naming the blank. AAR's longest
 *  real caption — "OWNER OR AUTHORIZED REPRESENTATIVE'S SIGNATURE" — is
 *  46 characters. */
const MAX_CAPTION_CHARS = 64;

/** How much of the shorter of (rule, caption) must overlap horizontally
 *  for a caption above or below to be about THAT rule. Loose, because a
 *  caption is often narrower than its line and sits at its left end. */
const MIN_OVERLAP_RATIO = 0.2;

interface CaptionMatch {
  span: SpanInput;
  /** Higher is more certain. Only compared within one blank. */
  score: number;
}

function horizontalOverlap(a: { x: number; width: number }, b: { x: number; width: number }): number {
  const left = Math.max(a.x, b.x);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const overlap = right - left;
  if (overlap <= 0) return 0;
  const shorter = Math.min(a.width, b.width);
  return shorter > 0 ? overlap / shorter : 0;
}

/**
 * The caption naming a blank, or null.
 *
 * Four shapes, in a fixed order of trust:
 *
 *   1. A tight label immediately left of the rule on the same line.
 *      Unambiguous: there is exactly one thing right before a blank.
 *   2. A caption directly BELOW. This is AAR's house style and the
 *      shape the old text rung had no concept of at all.
 *   3. A label further left on the same line.
 *   4. A caption directly ABOVE.
 *
 * Below must beat above, and that ordering is the whole reason a
 * stacked signature block works. Given
 *
 *     ______________________
 *     OWNER'S SIGNATURE
 *     ______________________
 *     OWNER'S PRINTED NAME
 *
 * the second rule's caption is the one under it. Reading upward would
 * label the printed-name line "signature", and a signature box where a
 * name goes is a wrong field that looks completely deliberate.
 *
 * Returns null rather than reaching further. An uncaptioned blank is
 * not a failure here — it becomes an outline the advisor clicks once,
 * which is a better answer than a confidently mislabelled field.
 */
export function captionForBlank(
  blank: DetectedBlank,
  spans: readonly SpanInput[],
): SpanInput | null {
  let best: CaptionMatch | null = null;
  const line = { x: blank.x, width: blank.width };

  for (const span of spans) {
    if (span.pageNumber !== blank.pageNumber) continue;
    const text = span.text.trim();
    if (!text || text.length > MAX_CAPTION_CHARS) continue;
    // A run of underscores or punctuation names nothing.
    if (!/[a-z]/i.test(text)) continue;
    // Nor does a signing service's stamp (see isProviderStamp).
    if (isProviderStamp(text)) continue;

    const spanRight = span.x + span.width;
    const spanBottom = span.y + span.height;

    // Same line as the blank? Compare against the rule itself, not the
    // invented field box — the box is tall and would call the line above
    // "same line".
    const onSameLine =
      span.y <= blank.lineY + 2 && spanBottom >= blank.lineY - span.height - 2;

    // A TICK BOX IS CAPTIONED ON ITS RIGHT. "☐ Sell   ☐ Rent" — the word
    // follows the square, which is the opposite of every other shape
    // here.
    //
    // Found by running this against a real PDF (2026-09-08): reading
    // leftward gave the Rent box the caption "Sell" and left the Sell
    // box with none, because the nearest text to the left of the second
    // square is the first square's own label. An election mislabelled by
    // one is not a near miss on a contract — it is the other answer.
    if (blank.kind === "box") {
      if (onSameLine) {
        const rightGap = span.x - (blank.x + blank.width);
        if (rightGap >= -2 && rightGap <= TIGHT_LEFT_GAP_PT) {
          best = better(best, { span, score: 4000 - rightGap });
          continue;
        }
      }
      // Nothing else. A checkbox with no label beside it stays an
      // unmapped blank rather than borrowing a caption from above or
      // below, where the nearest text is another row's election.
      continue;
    }

    if (onSameLine) {
      const gap = blank.x - spanRight;
      if (gap >= -2 && gap <= TIGHT_LEFT_GAP_PT) {
        best = better(best, { span, score: 4000 - gap });
        continue;
      }
      if (gap > TIGHT_LEFT_GAP_PT && gap <= WIDE_LEFT_GAP_PT) {
        best = better(best, { span, score: 2000 - gap });
        continue;
      }
    }

    const overlap = horizontalOverlap(line, span);
    if (overlap < MIN_OVERLAP_RATIO) continue;

    const gapBelow = span.y - blank.lineY;
    if (gapBelow >= -2 && gapBelow <= BELOW_GAP_PT) {
      best = better(best, { span, score: 3000 - gapBelow });
      continue;
    }

    const gapAbove = blank.lineY - spanBottom;
    if (gapAbove >= -2 && gapAbove <= ABOVE_GAP_PT) {
      best = better(best, { span, score: 1000 - gapAbove });
    }
  }

  return best?.span ?? null;
}

function better(current: CaptionMatch | null, candidate: CaptionMatch): CaptionMatch {
  return !current || candidate.score > current.score ? candidate : current;
}

// ─── Blanks to proposals ────────────────────────────────────────────

/** What to call a run on its chip. Its nearest caption is often the
 *  form's sub-clause letter ("8a.") printed in the margin beside the
 *  first line, which names nothing; the section heading a line or two
 *  above does. So a caption of fewer than four letters yields to the
 *  longest lettered line within two pitches above the run. */
function runLabelFor(
  blank: DetectedBlank & { lineCount: number; linePitch: number },
  caption: string,
  spans: readonly SpanInput[],
): string {
  const letters = (caption.match(/[a-z]/gi) ?? []).length;
  if (letters >= 4) return caption;
  let best: SpanInput | null = null;
  for (const span of spans) {
    if (span.pageNumber !== blank.pageNumber) continue;
    const text = span.text.trim();
    if (!HAS_LETTERS.test(text) || text.length > MAX_CAPTION_CHARS) continue;
    const gap = blank.lineY - (span.y + span.height);
    if (gap < 0 || gap > blank.linePitch * 2) continue;
    if (horizontalOverlap({ x: blank.x, width: blank.width }, span) < MIN_OVERLAP_RATIO) continue;
    if (!best || text.length > best.text.trim().length) best = span;
  }
  return best ? best.text.trim() : caption;
}

export interface BlankSuggestOptions {
  /** Enables data binding — what each blank is ASKING FOR. Omitted, a
   *  captioned blank still gets its ceremony type (signature, initials,
   *  date) and everything else arrives as an empty text box. */
  binding?: BindingLookup;
}

/**
 * The proposals a set of detected blanks earns.
 *
 * ONLY captioned blanks. Everything else is returned by
 * `unmappedBlanks()` instead, for the prep screen to outline — see the
 * header on why that split is what keeps this rung precise enough to
 * put fields on a legal document.
 */
export function suggestFromBlanks(
  blanks: readonly DetectedBlank[],
  spans: readonly SpanInput[],
  options: BlankSuggestOptions = {},
): SuggestedField[] {
  const out: SuggestedField[] = [];
  const roleOccurrences = new Map<string, number>();

  for (const blank of blanks) {
    const caption = captionForBlank(blank, spans);
    // A RUN NEEDS NO CAPTION TO BE PROPOSED (Grant, 2026-09-29: "the e
    // sign feature also needs to recognize multiline areas and make them
    // that as default rather than one field per line"). Two or more
    // empty rules stacked at one pitch with nothing between them is the
    // form saying "write here" more plainly than any caption could, and
    // the lines AAR heads with a bare "$" have no lettered caption at
    // all. A single uncaptioned rule is still only an outline — see the
    // header — because on its own it could be furniture.
    if (!caption && !isRun(blank)) continue;
    const label = caption?.text.trim() ?? "";

    if (blank.kind === "box") {
      // A checkbox's caption is its election ("Sell", "Rent"). We know
      // WHERE it is exactly — the form drew the square — but never what
      // the right answer is, so it arrives unchecked for a person to
      // decide, which is what an election on a contract should be.
      out.push({
        fieldType: "checkbox",
        pageNumber: blank.pageNumber,
        x: blank.x,
        y: blank.y,
        width: blank.width,
        height: blank.height,
        source: "ruled_line",
        // The document drew this square. Only its MEANING is inferred.
        confidence: 0.8,
        reason: `Checkbox — ${label} · page ${blank.pageNumber}`,
        signerHint: null,
      });
      continue;
    }

    // What a person here already decided this caption is beats what the
    // built-in rules infer — a lesson that never wins is not a lesson.
    // Only ever a ceremony type; see learnedCeremonyForLabel.
    const taughtType = options.binding
      ? learnedCeremonyForLabel(label, options.binding)
      : null;
    const ceremony = taughtType
      ? { type: taughtType, what: ceremonyTypeForLabel(label)?.what ?? label }
      : ceremonyTypeForLabel(label);
    const bound = ceremony ? null : options.binding ? bindingForLabel(label, options.binding) : null;
    const type: EsignFieldType = ceremony?.type ?? "text";

    // THE FORM'S OWN TYPE SIZE, off the caption naming this blank
    // (Baker, 2026-09-18: "too big for the lines they are on").
    //
    // A caption and the value written on the line beside it are set in
    // the same type — that is what makes a filled form look filled
    // rather than annotated. pdf.js reports a span's height as very
    // nearly its point size, so the caption measures the value for us.
    //
    // TEXT ONLY, and the exclusion is real: a SIGNATURE box holds a
    // drawn mark, not type, so sizing it to 9-point small print would
    // leave nowhere to sign; a DATE SIGNED is stamped by the provider
    // at a size we are not setting. Both keep the heights they have
    // always had.
    const captionPt =
      caption &&
      Number.isFinite(caption.height) &&
      caption.height >= MIN_FIELD_FONT_PT &&
      caption.height <= MAX_FIELD_FONT_PT
        ? caption.height
        : null;

    // A RUN OF STACKED RULES IS ONE MULTI-LINE TEXT BOX, whatever its
    // caption says (Grant, 2026-09-29). Its geometry is the run's own —
    // the detector sized it so line k's rule is where multiline-text.ts
    // puts it — and it is never a ceremony type: nobody signs across
    // forty lines. A caption naming a record field still binds it (the
    // legal description flows into its three lines).
    if (isRun(blank)) {
      // Set in the page's BODY type, not the caption's: a section
      // heading captions the run in 13-point capitals and the terms
      // under it are typed in 9. The caption's size is the fallback for
      // a page whose body could not be measured.
      const bodyPt = dominantTypeSize(spans, blank.pageNumber);
      const runLabel = runLabelFor(blank, label, spans) || "Lines to write on";
      out.push({
        fieldType: "text",
        ...(bound ? { mergeToken: bound.token } : {}),
        ...(bodyPt ?? captionPt ? { fontSize: (bodyPt ?? captionPt) as number } : {}),
        multiline: true,
        linePitch: blank.linePitch,
        ...(blank.firstLineIndent ? { firstLineIndent: blank.firstLineIndent } : {}),
        pageNumber: blank.pageNumber,
        x: blank.x,
        y: blank.y,
        width: blank.width,
        height: blank.height,
        source: "ruled_line",
        confidence: bound ? 0.8 : 0.65,
        reason: `${bound?.label ?? runLabel} · ${blank.lineCount} lines · page ${blank.pageNumber}`,
        signerHint: roleFromCaption(label, roleOccurrences),
      });
      continue;
    }

    // A SIGNATURE LINE WITH ITS DATE AT THE END (2026-10-13). AAR's
    // signature blocks are one rule with "MO/DA/YR" printed under its
    // right end and the signer's tick-row under its left: the signature
    // goes on the left and the date on the right, on the same rule.
    // Until now one rule got ONE caption, so the whole line became a
    // date (or a text box hinted "Seller"), and the signature itself was
    // left to Stew's guess — which is how Nate's Addendum showed a date
    // box drawn on top of the seller's signature.
    const split = signatureDateSplit(blank, spans);
    if (split) {
      const hint = roleFromCaption(split.captionText, roleOccurrences);
      out.push({
        fieldType: "signature",
        pageNumber: blank.pageNumber,
        x: blank.x,
        y: Math.max(0, blank.lineY - FIELD_HEIGHT.signature + RULE_OVERHANG_PT),
        width: split.dateX - blank.x - SPLIT_GAP_PT,
        height: FIELD_HEIGHT.signature,
        source: "ruled_line",
        confidence: 0.85,
        reason: `Signature line · page ${blank.pageNumber}`,
        signerHint: hint,
      });
      out.push({
        fieldType: "date_signed",
        pageNumber: blank.pageNumber,
        x: split.dateX,
        y: Math.max(0, blank.lineY - FIELD_HEIGHT.date_signed + RULE_OVERHANG_PT),
        width: blank.x + blank.width - split.dateX,
        height: FIELD_HEIGHT.date_signed,
        source: "ruled_line",
        confidence: 0.85,
        reason: `Date beside a signature · page ${blank.pageNumber}`,
        signerHint: hint,
      });
      continue;
    }

    const height =
      type === "text" && captionPt
        ? Math.max(MIN_TEXT_BOX_PT, captionPt * LINE_BOX_RATIO)
        : FIELD_HEIGHT[type];

    out.push({
      fieldType: type,
      ...(bound ? { mergeToken: bound.token } : {}),
      ...(type === "text" && captionPt ? { fontSize: captionPt } : {}),
      pageNumber: blank.pageNumber,
      x: blank.x,
      // Re-seated for this type's height so a 28pt signature box and a
      // 24pt date box both sit ON the rule rather than one floating.
      y: Math.max(0, blank.lineY - height + RULE_OVERHANG_PT),
      // The rule's own width. The form measured this blank for us; a
      // default width would either overflow a short line or leave a long
      // one looking unfinished.
      width: blank.width,
      height,
      source: "ruled_line",
      // Above the text rung and below the document's own form fields.
      // The LINE is a fact the document states; what it is FOR is read
      // off a caption, which is a reading — so a recognized caption
      // scores higher than an unrecognized one.
      // A caption this company taught by hand is the strongest reading
      // available short of a full saved layout: somebody looked at this
      // exact wording on a real contract and said what it was.
      confidence: taughtType ? 0.9 : ceremony ? 0.85 : bound ? 0.8 : 0.65,
      reason: `${ceremony?.what ?? bound?.label ?? label} · page ${blank.pageNumber}`,
      signerHint: roleFromCaption(label, roleOccurrences),
    });
  }

  return out;
}

/** A rule this wide or wider can hold a signature and a date. AAR's
 *  side-by-side signature blocks are ~250pt; a 110pt line is a lone
 *  signature at most. */
const SPLIT_MIN_RULE_PT = 160;
const SPLIT_GAP_PT = 4;
/** The narrowest a date box may be: room for "10/13/2026". */
const SPLIT_MIN_DATE_PT = 60;
const DATE_FORMAT_CAPTION = /\bmo\s*\/\s*da\s*\/\s*yr\b|^\s*(mm|mo)\s*[/-]\s*(dd|da)\s*[/-]\s*(yy|yyyy|yr)\s*$/i;
/** How far under a rule its captions are printed. */
const SPLIT_CAPTION_DEPTH_PT = 16;

/**
 * Where a signature line's date starts, when the rule is a signature
 * block: wide, with a date-format caption under its right part. Null
 * otherwise — a lone date line, a short line, or a "Contract dated
 * ____" blank whose date caption sits under the middle.
 *
 * `captionText` is every other caption under the rule, joined — the
 * words that say whose block it is ("BUYER'S SIGNATURE", or AAR's
 * "Seller Buyer Landlord Tenant" tick-row, which says nobody's).
 */
export function signatureDateSplit(
  blank: DetectedBlank,
  spans: readonly SpanInput[],
): { dateX: number; captionText: string } | null {
  if (blank.kind !== "line" || isRun(blank)) return null;
  if (blank.width < SPLIT_MIN_RULE_PT) return null;
  const under = spans.filter((sp) => {
    if (sp.pageNumber !== blank.pageNumber) return false;
    const gap = sp.y - blank.lineY;
    if (gap < -2 || gap > SPLIT_CAPTION_DEPTH_PT) return false;
    const cx = sp.x + sp.width / 2;
    return cx >= blank.x && cx <= blank.x + blank.width;
  });
  const date = under.find((sp) => DATE_FORMAT_CAPTION.test(sp.text.trim()));
  if (!date) return null;
  const dateCenter = date.x + date.width / 2;
  if (dateCenter < blank.x + blank.width * 0.6) return null;
  const right = blank.x + blank.width;
  const dateX = Math.min(date.x - 8, right - SPLIT_MIN_DATE_PT);
  if (dateX - blank.x < 80) return null;
  // Words on the rule's own line, just left of it, make it a data blank
  // instead ("Contract dated ____ MO/DA/YR"), whatever is printed under.
  const ledByWords = spans.some(
    (sp) =>
      sp.pageNumber === blank.pageNumber &&
      /[a-z]{2,}/i.test(sp.text) &&
      !isProviderStamp(sp.text) &&
      sp.y <= blank.lineY + 2 &&
      sp.y + sp.height >= blank.lineY - sp.height - 2 &&
      blank.x - (sp.x + sp.width) >= -2 &&
      blank.x - (sp.x + sp.width) <= 12,
  );
  if (ledByWords) return null;
  const captionText = under
    .filter((sp) => sp !== date && /[a-z]/i.test(sp.text) && !isProviderStamp(sp.text))
    .map((sp) => sp.text.trim())
    .join(" ");
  return { dateX, captionText };
}

/**
 * PUT A GUESSED SIGNING MARK ON THE PAGE'S OWN LINE (2026-10-14).
 *
 * Stew places signatures and dates by reading the words around them,
 * so its boxes float: Nate's Addendum had a signature drawn over the
 * Seller/Buyer tick-boxes under its line, and a date hanging off the
 * right edge of the page. A signature or date within a line's reach of
 * a real rule (or a signing service's signature box) is moved onto it:
 * seated on the rule and kept inside its ends. Text boxes are left as
 * they are.
 */
const SNAP_REACH_PT = 22;

export function snapSigningMarksToRules<
  T extends { fieldType: EsignFieldType; pageNumber: number; x: number; y: number; width: number; height: number },
>(proposals: readonly T[], blanks: readonly DetectedBlank[]): T[] {
  const rules = blanks.filter((b) => b.kind === "line" && !isRun(b));
  return proposals.map((p) => {
    if (p.fieldType !== "signature" && p.fieldType !== "date_signed" && p.fieldType !== "initials") return p;
    const bottom = p.y + p.height;
    const best = rules
      .filter((r) => r.pageNumber === p.pageNumber && Math.abs(r.lineY - bottom) <= SNAP_REACH_PT)
      .map((r) => ({
        r,
        overlap: Math.min(r.x + r.width, p.x + p.width) - Math.max(r.x, p.x),
        dist: Math.abs(r.lineY - bottom),
      }))
      .filter((c) => c.overlap > Math.min(p.width, c.r.width) * 0.3)
      .sort((a, b) => b.overlap - a.overlap || a.dist - b.dist)[0];
    if (!best) return p;
    const { r } = best;
    const width = Math.min(p.width, r.width);
    const x = Math.min(Math.max(p.x, r.x), r.x + r.width - width);
    return { ...p, x, width, y: Math.max(0, r.lineY - p.height + RULE_OVERHANG_PT) };
  });
}

// ─── Blocks somebody has already signed ─────────────────────────────
//
// A contract the other side sent back has their signatures on it.
// dotloop prints "dotloop verified 10/12/26 11:35 AM MST VFXU-LROZ-…"
// beside each one, DocuSign "DocuSigned by:". Nate's Addendum arrived
// with the buyer's block signed, and the OS proposed a signature for
// the seller on top of the buyer's (2026-10-13). A signing mark on a
// block that already carries one of these is dropped, from every rung
// and from a recognized form's map alike.

const SIGNED_STAMP =
  /\b(dotloop\s+verified|docusigned\s+by|digitally\s+signed|electronically\s+signed|authentisign\s+id)\b|\b[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{2,4}\b/i;

export function isSignedStamp(text: string): boolean {
  return SIGNED_STAMP.test(text);
}

/** How far above its rule a verification stamp sits (dotloop prints
 *  it in two or three small lines just over the signature). */
const STAMP_ABOVE_RULE_PT = 30;

export function dropSigningMarksOnSignedBlocks<
  T extends { fieldType: EsignFieldType; pageNumber: number; x: number; y: number; width: number; height: number },
>(proposals: readonly T[], spans: readonly SpanInput[]): { kept: T[]; droppedCount: number } {
  const stamps = spans.filter((sp) => isSignedStamp(sp.text));
  if (stamps.length === 0) return { kept: [...proposals], droppedCount: 0 };
  const kept: T[] = [];
  let droppedCount = 0;
  for (const p of proposals) {
    const signing = p.fieldType === "signature" || p.fieldType === "initials" || p.fieldType === "date_signed";
    if (!signing) {
      kept.push(p);
      continue;
    }
    // The block a mark belongs to is its own line: the box's bottom is
    // the rule. A stamp printed over that rule, anywhere along the
    // block's run of the line, signs every mark on it.
    const rule = p.y + p.height;
    const signed = stamps.some((st) => {
      if (st.pageNumber !== p.pageNumber) return false;
      const stampBottom = st.y + st.height;
      if (stampBottom < rule - STAMP_ABOVE_RULE_PT - p.height || st.y > rule + 4) return false;
      const left = Math.max(st.x, p.x - 12);
      const right = Math.min(st.x + st.width, p.x + p.width + 12);
      return right > left;
    });
    if (signed) {
      droppedCount += 1;
      continue;
    }
    kept.push(p);
  }
  return { kept, droppedCount };
}

// ─── For Broker Use Only ────────────────────────────────────────────
//
// Grant, 2026-10-13: "for broker use only, needs to always be left
// blank. for sure not the customers info but this always needs to be
// left blank." Every AAR form ends with that box (file number, manager's
// and broker's initials, a date), and every rung read its lines as
// blanks to fill — Nate's Addendum put four boxes, stacked, on it.
//
// The region is found from the page: the caption, then the drawn box
// around it when there is one, else a band under the caption across the
// page. Nothing whose middle falls inside it is ever proposed, outlined,
// asked about, or placed from a map.

const OFFICE_USE_CAPTION =
  /\b(for\s+(broker|brokerage|office)('s)?\s+use(\s+only)?|office\s+use\s+only)\b/i;

export interface PageRegion {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** How tall a region with no drawn box around it is taken to be: the
 *  caption's line and the one under it, which is all AAR ever prints. */
const OFFICE_USE_BAND_PT = 44;
const OFFICE_USE_MAX_BOX_PT = 120;

export function officeUseRegions(
  spans: readonly SpanInput[],
  paths: readonly VectorPathInput[],
  pages: readonly { pageNumber: number; width: number }[],
): PageRegion[] {
  const out: PageRegion[] = [];
  for (const sp of spans) {
    if (!OFFICE_USE_CAPTION.test(sp.text)) continue;
    const pageWidth = pages.find((p) => p.pageNumber === sp.pageNumber)?.width ?? 612;
    // The smallest drawn rectangle around the caption, when the form
    // boxes the area (AAR does). A thin rule is not a box.
    const box = paths
      .filter(
        (pa) =>
          pa.pageNumber === sp.pageNumber &&
          pa.height >= 12 &&
          pa.height <= OFFICE_USE_MAX_BOX_PT &&
          pa.x <= sp.x + 1 &&
          pa.y <= sp.y + 1 &&
          pa.x + pa.width >= sp.x + sp.width - 1 &&
          pa.y + pa.height >= sp.y + sp.height - 1,
      )
      .sort((a, b) => a.width * a.height - b.width * b.height)[0];
    if (box) {
      out.push({ pageNumber: sp.pageNumber, x: box.x, y: box.y, width: box.width, height: box.height });
      continue;
    }
    out.push({
      pageNumber: sp.pageNumber,
      x: 0,
      y: Math.max(0, sp.y - 4),
      width: pageWidth,
      height: OFFICE_USE_BAND_PT,
    });
  }
  return out;
}

/** Whether a box's middle falls inside any of the regions. */
export function insideRegions(
  box: { pageNumber: number; x: number; y: number; width: number; height: number },
  regions: readonly PageRegion[],
): boolean {
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  return regions.some(
    (r) =>
      r.pageNumber === box.pageNumber &&
      cx >= r.x &&
      cx <= r.x + r.width &&
      cy >= r.y &&
      cy <= r.y + r.height,
  );
}

/**
 * The blanks nothing has claimed — what the screen outlines and counts.
 *
 * A blank is claimed when a field already covers it, whatever placed
 * that field: this rung, the AcroForm rung, Stew, a saved layout, or a
 * person dragging a box. So the count answers the question an advisor
 * actually has — "is there anything left on this form?" — rather than
 * "did MY rung find everything?".
 */
export function unmappedBlanks(
  blanks: readonly DetectedBlank[],
  placed: readonly { pageNumber: number; x: number; y: number; width: number; height: number }[],
): DetectedBlank[] {
  return blanks.filter((b) => {
    if (placed.some((f) => coversBlank(f, b))) return false;
    // A run whose rules are claimed one by one — a map from before the
    // OS read runs still carries one box per line — is mapped too, once
    // at least half its rules have a box on them.
    if (isRun(b)) {
      const claimed = new Set<number>();
      for (const f of placed) {
        const k = ruleIndexUnder(f, b);
        if (k !== null && horizontalOverlap(f, { x: b.x, width: b.width }) >= 0.25) claimed.add(k);
      }
      if (claimed.size >= Math.ceil(b.lineCount / 2)) return false;
    }
    return true;
  });
}

/** Which of a run's rules a box sits on: the one nearest the box's own
 *  seat (its bottom edge less the overhang), and only when that is
 *  within half a pitch. A 24pt box on a 16pt pitch overlaps two rules
 *  geometrically; it SITS on exactly one, and this says which. */
function ruleIndexUnder(
  field: { pageNumber: number; y: number; height: number },
  run: DetectedBlank & { lineCount: number; linePitch: number },
): number | null {
  if (field.pageNumber !== run.pageNumber) return null;
  const seat = field.y + field.height - MULTILINE_RULE_OVERHANG_PT;
  const rules = multilineRuleYs({ y: run.y, height: run.height, linePitch: run.linePitch });
  let best: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  rules.forEach((rule, k) => {
    const d = Math.abs(rule - seat);
    if (d < bestDistance) {
      bestDistance = d;
      best = k;
    }
  });
  return best !== null && bestDistance <= run.linePitch / 2 ? best : null;
}

/** A field claims a blank when it sits on the same line and overlaps it
 *  horizontally. Vertical tolerance is generous because a field placed
 *  by hand is nudged by eye, and calling one blank "still unmapped"
 *  when a person has plainly just filled it is the way this count would
 *  lose its credibility fastest. */
function coversBlank(
  field: { pageNumber: number; x: number; y: number; width: number; height: number },
  blank: DetectedBlank,
): boolean {
  if (field.pageNumber !== blank.pageNumber) return false;
  if (horizontalOverlap(field, { x: blank.x, width: blank.width }) < 0.25) return false;
  // A run is claimed when one field spans it, or when its rules are
  // individually claimed — a map that still carries one box per line
  // has mapped the area, whatever this screen would draw for it.
  if (isRun(blank)) {
    const bottom = field.y + field.height;
    const overlap = Math.min(bottom, blank.y + blank.height) - Math.max(field.y, blank.y);
    if (overlap >= blank.height * 0.5) return true;
    return false;
  }
  return coversRule(field, blank.lineY);
}

/** A field sits on a rule when the rule falls inside its vertical span,
 *  give or take a hand's nudge. */
function coversRule(field: { y: number; height: number }, ruleY: number): boolean {
  return ruleY >= field.y - 8 && ruleY <= field.y + field.height + 8;
}

/** A blank that is a run of stacked rules — see DetectedBlank.lineCount. */
export function isRun(
  blank: DetectedBlank,
): blank is DetectedBlank & { lineCount: number; linePitch: number } {
  return (
    typeof blank.lineCount === "number" &&
    blank.lineCount >= 2 &&
    typeof blank.linePitch === "number" &&
    blank.linePitch > 0
  );
}

/**
 * Fold one-box-per-line proposals that sit on a stacked run into ONE
 * multi-line box spanning the run (Grant, 2026-09-29).
 *
 * The purchase contract's saved map was taught before the OS knew a box
 * could span lines, so it opens with five separate "Text" boxes on lines
 * 12 to 16 and the atlas's forty-six-line box printing as one centred
 * line. The detector now reads those runs; this is how a map from
 * before it existed catches up without anyone redrawing it, and saving
 * the map afterwards persists the fold.
 *
 * DELIBERATELY NARROW. Only proposals fold — nothing a person placed or
 * adjusted — and only when every box on the run is an EMPTY, UNBOUND
 * text box belonging to the same owner, each sitting on exactly one
 * rule, with at least two of them and no rule claimed twice. A box with
 * a value or a binding is a decision somebody made about that line and
 * is left exactly where it is; a "$" box and a text box sharing a rule
 * means the form wants two things on that line. Every proposal that is
 * not folded comes back untouched, in its original order.
 */
export function foldStackedProposals<
  T extends {
    fieldType: EsignFieldType;
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
    signerKey?: string | undefined;
    roleKey?: string | null | undefined;
    value?: string | null | undefined;
    mergeToken?: string | null | undefined;
    multiline?: boolean | undefined;
    linePitch?: number | undefined;
    firstLineIndent?: number | undefined;
  },
>(proposals: readonly T[], blanks: readonly DetectedBlank[]): { fields: T[]; foldedRuns: number } {
  const dropped = new Set<T>();
  const replaced = new Map<T, T>();
  let foldedRuns = 0;
  for (const blank of blanks) {
    if (!isRun(blank)) continue;
    // Which proposals sit on which rule of this run.
    const onRule = new Map<number, T[]>();
    let usable = true;
    for (const p of proposals) {
      if (dropped.has(p) || replaced.has(p)) continue;
      if (p.pageNumber !== blank.pageNumber) continue;
      if (horizontalOverlap(p, { x: blank.x, width: blank.width }) < 0.25) continue;
      // A box already spanning several rules is either the fold itself
      // or a person's own multi-line box: the run is mapped, leave it.
      if (p.multiline || p.height > blank.linePitch * 1.9) {
        if (p.y < blank.y + blank.height && p.y + p.height > blank.y) {
          usable = false;
          break;
        }
        continue;
      }
      const k = ruleIndexUnder(p, blank);
      if (k === null) continue;
      if (p.fieldType !== "text" || p.mergeToken || (p.value ?? "").trim()) {
        usable = false;
        break;
      }
      const list = onRule.get(k) ?? [];
      list.push(p);
      onRule.set(k, list);
    }
    if (!usable || onRule.size < 2) continue;
    const members = [...onRule.values()].flat();
    if ([...onRule.values()].some((l) => l.length > 1)) continue;
    const owner = members[0]!;
    if (members.some((m) => m.signerKey !== owner.signerKey || (m.roleKey ?? null) !== (owner.roleKey ?? null))) {
      continue;
    }
    const first = [...members].sort((a, b) => a.y - b.y)[0]!;
    replaced.set(first, {
      ...first,
      x: blank.x,
      y: blank.y,
      width: blank.width,
      height: blank.height,
      multiline: true,
      linePitch: blank.linePitch,
      ...(blank.firstLineIndent ? { firstLineIndent: blank.firstLineIndent } : {}),
    });
    for (const m of members) if (m !== first) dropped.add(m);
    foldedRuns += 1;
  }
  return {
    fields: proposals.filter((p) => !dropped.has(p)).map((p) => replaced.get(p) ?? p),
    foldedRuns,
  };
}

/**
 * A text box someone drew or stretched across a run of printed rules
 * becomes the multi-line box that run is (Nate, 2026-10-15: "This text
 * box is starting at the middle of the page where other large textboxes
 * start at the first line").
 *
 * A box made from the run itself already knows its pitch. One drawn by
 * hand with the Text tool and pulled tall does not, so it was treated as
 * a one-line box: the editor centred its type top to bottom, and what
 * was typed printed as one centred line. This seats it on the rules
 * under it instead: the top and bottom snap to whole lines of the run,
 * the pitch is the run's, and the person's own left and right edges
 * stay where they put them.
 *
 * Returns the patch to apply, or null when the box covers fewer than
 * two rules of any run (an ordinary one-line box, or a tall box over no
 * printed lines, which keeps its size and simply starts at the top).
 */
export function seatOnRuledRun(
  field: {
    fieldType: EsignFieldType;
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
    multiline?: boolean | null | undefined;
    linePitch?: number | null | undefined;
    strike?: boolean | null | undefined;
  },
  blanks: readonly DetectedBlank[],
): { y: number; height: number; multiline: true; linePitch: number; firstLineIndent?: number } | null {
  if (field.fieldType !== "text" || field.strike) return null;
  if (field.multiline === true && typeof field.linePitch === "number") return null;
  let best: { blank: DetectedBlank & { lineCount: number; linePitch: number }; covered: number[]; all: number[] } | null =
    null;
  for (const blank of blanks) {
    if (!isRun(blank) || blank.pageNumber !== field.pageNumber) continue;
    if (blank.linePitch < MIN_LINE_PITCH_PT || blank.linePitch > MAX_LINE_PITCH_PT) continue;
    if (horizontalOverlap(field, { x: blank.x, width: blank.width }) < 0.5) continue;
    const all = multilineRuleYs({ y: blank.y, height: blank.height, linePitch: blank.linePitch });
    // An ordinary one-line box sitting on a rule is not a writing area,
    // however close the next rule happens to be: the same "spans several
    // rules" bar foldStackedProposals uses.
    if (field.height <= blank.linePitch * 1.9) continue;
    // A rule belongs to the box when the line of writing on it is inside
    // the box: at least half a line below the box's top, and no more than
    // a hand's slop past its bottom edge.
    const covered = all.filter(
      (r) => r >= field.y + blank.linePitch * 0.5 && r <= field.y + field.height + blank.linePitch * 0.25,
    );
    if (covered.length < 2) continue;
    if (!best || covered.length > best.covered.length) best = { blank, covered, all };
  }
  if (!best) return null;
  const { blank, covered, all } = best;
  const box = multilineBoxFromRules(covered[0]!, covered.length, blank.linePitch);
  // The first-line indent clears a caption printed on the run's FIRST
  // rule, measured from the run's left edge, so it only carries over
  // when the box starts on that rule and from that edge.
  const indent =
    blank.firstLineIndent && covered[0] === all[0] && Math.abs(field.x - blank.x) < 4
      ? blank.firstLineIndent - (field.x - blank.x)
      : 0;
  return {
    y: box.y,
    height: box.height,
    multiline: true,
    linePitch: blank.linePitch,
    ...(indent > 0 && indent < field.width ? { firstLineIndent: indent } : {}),
  };
}

/** Signer roles a caption names out loud come from the one shared list
 *  in field-suggestions.ts (ROLE_WORDS); this rung kept its own copy in
 *  a different order until 2026-10-13. */

/**
 * Which occurrence of each role word this pass has already seen — see
 * the twin counter in field-suggestions.ts (RoleOccurrenceCounts) for
 * why. A ruled form is the case this matters most for: "BUYER
 * SIGNATURE" drawn twice, once per buyer, with no printed number on
 * either line, is exactly the shape of Nate's Wire Fraud Advisory and
 * Buyer Advisory sends on 2026-09-23 — both signature lines came back
 * captioned for the same buyer.
 */
function roleFromCaption(text: string, occurrences: Map<string, number>): string | null {
  // One reading for both rungs (2026-10-13) — see roleWordFromCaption.
  return roleWordFromCaption(text, occurrences);
}

// ─── A blank that is already written on is not a blank ──────────────
//
// Nate Swartz, 2026-09-25, on a lender's pre-approval he uploaded
// already filled in: "I certainly do not need to edit 100 fields. It
// looks like this would require the client to fill out all of these
// fields." He was right. Every rung above reads WHERE a form left room
// to write, and none of them asked whether somebody already had. So a
// finished form got a required text box on top of every answer the
// lender typed, an empty checkbox on top of every box already ticked,
// and Accept all would have handed the buyer a hundred boxes to type
// into before she could sign.
//
// The test is the page's own text. A value typed onto a form sits ON
// its rule, inside the box we would have drawn there; a caption sits
// beside it, below it, or to its left, never centred inside it. So a
// span whose centre falls inside the box, carrying at least one letter,
// digit or mark (a run of underscores is the blank itself, not writing
// on it), means the answer is already there.
//
// SIGNING CEREMONY BOXES ARE NEVER DROPPED. A signature, initials or
// date-signed line is the thing the form is being sent FOR, and a
// signature line can legitimately carry a typed name or "X" beside the
// mark. Losing one of those is far worse than keeping one extra.
//
// What this cannot see: handwriting on a scan, which carries no text at
// all. That case is why the prep screen also offers "Signatures & dates
// only" — one click, whatever the page is made of.

/** Field types that are the signing itself, kept on any form. */
const CEREMONY_TYPES: ReadonlySet<EsignFieldType> = new Set([
  "signature",
  "initials",
  "date_signed",
]);

/** The printed-name line beside a signature is part of the signing
 *  block too (Nate: "unless it is a signature, name, or date block"). */
const SIGNER_NAME_TOKENS: ReadonlySet<string> = new Set(["contact.legal_full_name"]);

/** True for what a signer does at signing — their signature, initials,
 *  the date, and their printed name. Everything else on a form someone
 *  already completed is an answer, not a signing step. */
export function isSigningBlockField(f: {
  fieldType: EsignFieldType;
  mergeToken?: string | null | undefined;
}): boolean {
  if (CEREMONY_TYPES.has(f.fieldType)) return true;
  return f.fieldType === "text" && !!f.mergeToken && SIGNER_NAME_TOKENS.has(f.mergeToken);
}

/** Letters, digits, or a check mark / cross glyph. Underscores, dots
 *  and dashes are the blank drawn in type, not anything written on it. */
const WRITTEN_MARK = /[\p{L}\p{N}✓✔✕✖✗✘☒☑]/u;

/** Whether the page already carries writing inside this box. */
export function isAlreadyWrittenOn(
  box: { pageNumber: number; x: number; y: number; width: number; height: number },
  spans: readonly SpanInput[],
): boolean {
  for (const span of spans) {
    if (span.pageNumber !== box.pageNumber) continue;
    if (!WRITTEN_MARK.test(span.text)) continue;
    const cx = span.x + span.width / 2;
    const cy = span.y + span.height / 2;
    if (cx < box.x || cx > box.x + box.width) continue;
    if (cy < box.y || cy > box.y + box.height) continue;
    return true;
  }
  return false;
}

/**
 * Split proposals into the ones to place and a count of those dropped
 * because the page already answers them. Signing-block fields always
 * stay; see the header above.
 */
export function dropProposalsOnWrittenBlanks<
  T extends {
    fieldType: EsignFieldType;
    mergeToken?: string | null | undefined;
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
  },
>(proposals: readonly T[], spans: readonly SpanInput[]): { kept: T[]; droppedCount: number } {
  const kept: T[] = [];
  let droppedCount = 0;
  for (const p of proposals) {
    if (!isSigningBlockField(p) && isAlreadyWrittenOn(p, spans)) {
      droppedCount += 1;
      continue;
    }
    kept.push(p);
  }
  return { kept, droppedCount };
}
